/**
 * CitationList — the "Sources" of an AI answer: the cited PDF pages (with the
 * cited region highlighted), video scenes and audio clips, each opening the
 * original document. Rendered under the assistant message.
 */

import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useThemeClasses } from "../../providers/ThemeProvider";
import { cx } from "../../styles";
import {
  type Citation,
  type CitationBBox,
  bboxStyle,
  citationKey,
  clipRange,
  clipUrl,
  documentLink,
  documentName,
  imageUrl,
  isExpired,
  isMediaClip,
  isSummary,
  pdfPage,
  pickDefaultSubtitle,
  positionLabel,
  subtitleLanguageName,
} from "./citations";
import { useCitationViewer } from "./citationViewerStore";
import {
  type SubtitleStatus,
  type SubtitleTrack,
  isKiragUrl,
  loadCitationSubtitles,
  refreshSubtitleUrl,
} from "../../services/KiRagService";

const COLLAPSED_COUNT = 4;

/**
 * Size of the media in the dialog: any aspect ratio (portrait phone video,
 * landscape, square, tall PDF page) fits the width and the viewport height,
 * leaving room for the header, the subtitle row and the excerpt, so the
 * controls never need a scroll. w/h auto + max-w/max-h keep the ratio.
 */
export const VIEWER_MEDIA_FIT =
  "block max-w-full w-auto h-auto object-contain max-h-[max(12rem,min(70vh,calc(90vh-16rem)))]";

// ─── Image with the cited region highlighted ─────────────────────────────────

// With `fit` (dialog) the wrapper shrink-wraps the rendered image, so the
// percentage bbox overlay stays on the image box; without it (card) the image
// fills the card width.
const HighlightedImage: React.FC<{
  src: string;
  alt: string;
  bbox?: CitationBBox | null;
  className?: string;
  fit?: boolean;
  onExpired?: () => void;
}> = ({ src, alt, bbox, className, fit, onExpired }) => {
  const box = bboxStyle(bbox ?? null);
  return (
    <div className={cx("relative", fit && "max-w-full", className)}>
      <img
        src={src}
        alt={alt}
        className={fit ? VIEWER_MEDIA_FIT : "block w-full h-auto"}
        loading="lazy"
        onError={onExpired}
      />
      {box && (
        <div
          className="absolute rounded-sm border-2 border-amber-400 bg-amber-300/25 pointer-events-none"
          style={box}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

// ─── Subtitles of a cited video ──────────────────────────────────────────────
// The subtitles that already exist in KI RAG, read like the KI RAG web app
// does (translations list, then each current request's detail). An expired
// signed track URL is renewed once; a track that still fails is dropped.
type ViewerSubtitleStatus = SubtitleStatus | "loading";

function useCitationSubtitles(citation: Citation, enabled: boolean) {
  const [tracks, setTracks] = useState<SubtitleTrack[]>([]);
  const [loaded, setLoaded] = useState<ViewerSubtitleStatus>(
    enabled ? "loading" : "off",
  );
  const refreshed = useRef(new Set<string>());
  useEffect(() => {
    setTracks((ts) => (ts.length ? [] : ts));
    refreshed.current.clear();
    if (!enabled) {
      setLoaded("off");
      return undefined;
    }
    setLoaded("loading");
    const ctrl = new AbortController();
    loadCitationSubtitles(citation, ctrl.signal).then((r) => {
      if (ctrl.signal.aborted) return;
      if (r.tracks.length > 0) setTracks(r.tracks);
      setLoaded(r.status);
    });
    return () => ctrl.abort();
  }, [citation, enabled]);
  // Every track failed to load (and could not be renewed).
  const status: ViewerSubtitleStatus =
    loaded === "ready" && tracks.length === 0 ? "unavailable" : loaded;
  const onTrackError = useCallback(
    async (translationId: string) => {
      const drop = () =>
        setTracks((ts) => ts.filter((t) => t.translationId !== translationId));
      if (refreshed.current.has(translationId)) return drop();
      refreshed.current.add(translationId);
      const src = await refreshSubtitleUrl(citation, translationId);
      if (!src) return drop();
      setTracks((ts) =>
        ts.map((t) => (t.translationId === translationId ? { ...t, src } : t)),
      );
    },
    [citation],
  );
  // The renewed URL loaded, so a later expiry may renew again.
  const onTrackLoad = useCallback((translationId: string) => {
    refreshed.current.delete(translationId);
  }, []);
  return { tracks, status, onTrackError, onTrackLoad };
}

// ─── Subtitle language select and status ─────────────────────────────────────
// Native tracks stay in the player's CC menu; on a narrow (portrait) player
// Chrome hides CC in the ⋮ menu, so a visible select drives the same
// textTracks modes and follows a choice made in the native menu.
const SubtitleControls: React.FC<{
  videoRef: React.RefObject<HTMLVideoElement | null>;
  tracks: SubtitleTrack[];
  initial?: string;
  labelOf: (language: string) => string;
}> = ({ videoRef, tracks, initial, labelOf }) => {
  const { t } = useTranslation();
  const tc = useThemeClasses();
  const id = useId();
  const [selected, setSelected] = useState(initial ?? "");
  const value = tracks.some((tr) => tr.language === selected) ? selected : "";

  useEffect(() => {
    const list = videoRef.current?.textTracks;
    if (!list) return;
    for (let i = 0; i < list.length; i++) {
      const tt = list[i];
      if (tt.kind !== "subtitles") continue;
      const mode = value && tt.language === value ? "showing" : "disabled";
      if (tt.mode !== mode) tt.mode = mode;
    }
  }, [videoRef, tracks, value]);

  useEffect(() => {
    const list = videoRef.current?.textTracks;
    if (!list?.addEventListener) return undefined;
    const onChange = () => {
      let showing = "";
      for (let i = 0; i < list.length; i++) {
        const tt = list[i];
        if (tt.kind === "subtitles" && tt.mode === "showing")
          showing = tt.language;
      }
      setSelected(showing);
    };
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [videoRef]);

  return (
    <div className="flex items-center gap-2 text-xs">
      <label htmlFor={id} className={cx("font-medium", tc.textSecondary)}>
        {t("cockpit.subtitles", "Subtitles")}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => setSelected(e.target.value)}
        className={cx(
          "rounded border px-1.5 py-0.5 text-xs",
          tc.bgPrimary,
          tc.borderPrimary,
          tc.textPrimary,
        )}
      >
        <option value="">{t("cockpit.subtitlesOff", "Off")}</option>
        {tracks.map((tr) => (
          <option key={tr.translationId} value={tr.language}>
            {labelOf(tr.language)}
          </option>
        ))}
      </select>
    </div>
  );
};

const SubtitleStatusLine: React.FC<{ status: ViewerSubtitleStatus }> = ({
  status,
}) => {
  const { t } = useTranslation();
  const tc = useThemeClasses();
  const text: Partial<Record<ViewerSubtitleStatus, string>> = {
    loading: t("cockpit.subtitlesLoading", "Looking for subtitles…"),
    none: t("cockpit.subtitlesNone", "No subtitles for this video yet."),
    outdated: t(
      "cockpit.subtitlesOutdated",
      "The subtitles were made for an earlier version of this video. They can be translated again in KI RAG.",
    ),
    "no-speech": t(
      "cockpit.subtitlesNoSpeech",
      "This video has no speech to subtitle.",
    ),
    unavailable: t(
      "cockpit.subtitlesUnavailable",
      "Subtitles are unavailable right now.",
    ),
  };
  return (
    <p role="status" aria-live="polite" className={cx("text-xs", tc.textMuted)}>
      {text[status] ?? ""}
    </p>
  );
};

// Native <track> with its error/load events (listened to directly, React
// does not attach them on <track>). The src may change after a renewal.
const SubtitleTrackElement: React.FC<{
  track: SubtitleTrack;
  label: string;
  isDefault: boolean;
  onError: (translationId: string) => void;
  onLoad: (translationId: string) => void;
}> = ({ track, label, isDefault, onError, onLoad }) => {
  const ref = useRef<HTMLTrackElement>(null);
  const { translationId } = track;
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const err = () => onError(translationId);
    const load = () => onLoad(translationId);
    el.addEventListener("error", err);
    el.addEventListener("load", load);
    return () => {
      el.removeEventListener("error", err);
      el.removeEventListener("load", load);
    };
  }, [translationId, onError, onLoad]);
  return (
    <track
      ref={ref}
      kind="subtitles"
      src={track.src}
      srcLang={track.language}
      label={label}
      default={isDefault}
    />
  );
};

// ─── Video / audio clip at the cited scene ───────────────────────────────────
// Plain element source (signed KI RAG Content URL, no fetch/blob): the browser
// does the Range requests. Like KI RAG's player it seeks to start_ms when the
// metadata is loaded and pauses once at end_ms; after that the user can keep
// playing.
const ClipPlayer: React.FC<{
  kind: "video" | "audio";
  src: string;
  poster?: string;
  start?: number;
  end?: number;
  label: string;
  onExpired: () => void;
  subtitles?: React.ReactNode;
  videoRef?: React.Ref<HTMLVideoElement>;
  crossOrigin?: "anonymous";
}> = ({
  kind,
  src,
  poster,
  start,
  end,
  label,
  onExpired,
  subtitles,
  videoRef,
  crossOrigin,
}) => {
  const stopped = useRef(false);
  const onLoadedMetadata = (e: React.SyntheticEvent<HTMLMediaElement>) => {
    if (start !== undefined) e.currentTarget.currentTime = start;
  };
  const onTimeUpdate = (e: React.SyntheticEvent<HTMLMediaElement>) => {
    if (end === undefined || stopped.current) return;
    if (e.currentTarget.currentTime >= end) {
      stopped.current = true;
      e.currentTarget.pause();
    }
  };
  const common = {
    controls: true,
    preload: "metadata",
    src,
    "aria-label": label,
    onLoadedMetadata,
    onTimeUpdate,
    onError: onExpired,
  } as const;
  // Subtitle tracks are cross-origin KI RAG URLs, so a KI RAG-hosted video
  // gets crossorigin from the first render: changing it later restarts the
  // media load and the late tracks would not be picked up.
  return kind === "video" ? (
    <video
      {...common}
      ref={videoRef}
      poster={poster}
      crossOrigin={crossOrigin}
      className={cx(VIEWER_MEDIA_FIT, "bg-black")}
    >
      {subtitles}
    </video>
  ) : (
    <audio {...common} className="w-full" />
  );
};
// ─── Large view (dialog) ─────────────────────────────────────────────────────

const CitationViewer: React.FC<{ citation: Citation; onClose: () => void }> = ({
  citation,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const tc = useThemeClasses();
  const closeRef = useRef<HTMLButtonElement>(null);
  const img = imageUrl(citation);
  const clip = isMediaClip(citation);
  const clipSrc = clipUrl(citation);
  const { start, end } = clipRange(citation);
  const docExpired = isExpired(citation.document_url_expires_at);
  const docUrl = docExpired ? undefined : documentLink(citation);
  const page = pdfPage(citation);
  const hasClip = Boolean(clip && clipSrc);
  // The clip (original file) and the image (page image or keyframe) expire
  // independently; when the clip has expired the keyframe is still shown.
  const [clipExpired, setClipExpired] = useState(() => hasClip && docExpired);
  const [imgExpired, setImgExpired] = useState(
    () => Boolean(img) && isExpired(citation.media?.url_expires_at),
  );
  const showClip = hasClip && !clipExpired;
  const showImg = !showClip && Boolean(img) && !imgExpired;
  const showExpired =
    (hasClip && clipExpired) || (!showClip && Boolean(img) && imgExpired);
  const isVideo = showClip && clip === "video";
  const kiragVideo = isVideo && isKiragUrl(clipSrc);
  const videoRef = useRef<HTMLVideoElement>(null);
  const {
    tracks,
    status: subtitleStatus,
    onTrackError,
    onTrackLoad,
  } = useCitationSubtitles(
    citation,
    kiragVideo && Boolean(citation.document?.document_id),
  );
  const defaultLanguage = pickDefaultSubtitle(
    tracks.map((tr) => tr.language),
    i18n?.language,
  );
  const trackLabel = (language: string) =>
    t("cockpit.subtitleTrackLabel", "{{language}} (AI-generated)", {
      language: subtitleLanguageName(language),
    });
  const subtitles =
    tracks.length > 0
      ? tracks.map((tr) => (
          <SubtitleTrackElement
            key={tr.translationId}
            track={tr}
            label={trackLabel(tr.language)}
            isDefault={tr.language === defaultLanguage}
            onError={onTrackError}
            onLoad={onTrackLoad}
          />
        ))
      : undefined;
  const pos = isSummary(citation)
    ? t("cockpit.citationSummary", "Summary")
    : positionLabel(citation);
  const title = `${documentName(citation)}${pos ? ` · ${pos}` : ""}`;

  // Focus and Escape are set up once per opened dialog; onClose goes through a
  // ref so re-renders of the parent (streamed tokens) don't refocus Close.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) =>
      e.key === "Escape" && onCloseRef.current();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Keep Tab inside the modal dialog.
  const onDialogKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const focusable = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), select:not([disabled]), video[controls], audio[controls], [tabindex]:not([tabindex="-1"])',
      ),
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !e.currentTarget.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (
      !e.shiftKey &&
      (active === last || !e.currentTarget.contains(active))
    ) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cx(
          "flex w-full max-w-3xl max-h-[90vh] flex-col overflow-hidden rounded-xl shadow-2xl border",
          tc.bgPrimary,
          tc.borderPrimary,
        )}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onDialogKeyDown}
      >
        <div
          className={cx(
            "flex shrink-0 items-center gap-2 px-4 py-3 border-b",
            tc.borderPrimary,
          )}
        >
          <span className="text-xs font-semibold rounded bg-blue-500/15 text-blue-600 px-1.5 py-0.5">
            [{citation.index}]
          </span>
          <h4
            className={cx(
              "flex-1 min-w-0 truncate text-sm font-semibold",
              tc.textPrimary,
            )}
          >
            {title}
          </h4>
          {docUrl && (
            <a
              href={docUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-blue-600 hover:underline whitespace-nowrap"
            >
              {page !== undefined
                ? t("cockpit.openPdfAtPage", "Open PDF at page {{page}}", {
                    page,
                  })
                : t("cockpit.openDocument", "Open document")}
            </a>
          )}
          <button
            ref={closeRef}
            onClick={onClose}
            className={cx("p-1.5 rounded-lg", tc.textMuted, tc.hover)}
            aria-label={t("cockpit.close", "Close")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-3">
          {showClip && clip === "audio" && clipSrc ? (
            <ClipPlayer
              kind="audio"
              src={clipSrc}
              start={start}
              end={end}
              label={title}
              onExpired={() => setClipExpired(true)}
            />
          ) : showClip && clipSrc ? (
            <>
              <div
                data-testid="citation-media-stage"
                className="flex items-center justify-center overflow-hidden rounded-lg bg-black"
              >
                <ClipPlayer
                  kind="video"
                  src={clipSrc}
                  poster={imgExpired ? undefined : img}
                  start={start}
                  end={end}
                  label={title}
                  onExpired={() => setClipExpired(true)}
                  subtitles={subtitles}
                  videoRef={videoRef}
                  crossOrigin={kiragVideo ? "anonymous" : undefined}
                />
              </div>
              {tracks.length > 0 && (
                <SubtitleControls
                  videoRef={videoRef}
                  tracks={tracks}
                  initial={defaultLanguage}
                  labelOf={trackLabel}
                />
              )}
              {kiragVideo && <SubtitleStatusLine status={subtitleStatus} />}
            </>
          ) : showImg && img ? (
            <div
              data-testid="citation-media-stage"
              className="flex items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-black"
            >
              <HighlightedImage
                src={img}
                alt={title}
                bbox={citation.display?.bbox}
                fit
                onExpired={() => setImgExpired(true)}
              />
            </div>
          ) : null}
          {showExpired && (
            <p className={cx("text-sm", tc.textMuted)}>
              {t(
                "cockpit.citationExpired",
                "This preview has expired. Ask the question again to get fresh links.",
              )}
            </p>
          )}
          {citation.excerpt && (
            <blockquote
              className={cx(
                "max-h-24 overflow-y-auto border-l-2 border-blue-300 pl-3 text-sm whitespace-pre-wrap",
                tc.textSecondary,
              )}
            >
              {citation.excerpt}
            </blockquote>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── One source card ─────────────────────────────────────────────────────────

const CitationCard: React.FC<{
  citation: Citation;
  onOpen: (trigger: HTMLElement) => void;
}> = ({ citation, onOpen }) => {
  const { t } = useTranslation();
  const tc = useThemeClasses();
  const img = imageUrl(citation);
  const [expired, setExpired] = useState(
    () => Boolean(img) && isExpired(citation.media?.url_expires_at),
  );
  const clip = isMediaClip(citation);
  const name = documentName(citation);
  const pos = isSummary(citation)
    ? t("cockpit.citationSummary", "Summary")
    : positionLabel(citation);

  return (
    <button
      type="button"
      onClick={(e) => onOpen(e.currentTarget)}
      data-citation-key={citationKey(citation)}
      className={cx(
        "group/cite flex w-full min-w-0 flex-col overflow-hidden rounded-lg border text-left transition-shadow hover:shadow-md",
        tc.borderPrimary,
        tc.bgSurface,
      )}
      title={citation.excerpt}
      aria-label={`${t("cockpit.source", "Source")} ${citation.index}: ${name}${pos ? `, ${pos}` : ""}`}
    >
      <div className="relative w-full aspect-[4/3] overflow-hidden bg-slate-100 dark:bg-slate-800">
        {img && !expired ? (
          <HighlightedImage
            src={img}
            alt=""
            bbox={citation.display?.bbox}
            className="w-full"
            onExpired={() => setExpired(true)}
          />
        ) : (
          <p
            className={cx(
              "p-2 text-[10px] leading-snug line-clamp-6",
              tc.textMuted,
            )}
          >
            {expired
              ? t(
                  "cockpit.citationExpired",
                  "This preview has expired. Ask the question again to get fresh links.",
                )
              : citation.excerpt}
          </p>
        )}
        {clip && (
          <span
            className="absolute inset-0 flex items-center justify-center"
            aria-hidden="true"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white">
              <svg
                viewBox="0 0 16 16"
                fill="currentColor"
                className="ml-0.5 h-4 w-4"
              >
                <path d="M4 3.2v9.6a.6.6 0 00.9.5l7.6-4.8a.6.6 0 000-1L4.9 2.7a.6.6 0 00-.9.5z" />
              </svg>
            </span>
          </span>
        )}
        <span className="absolute left-1 top-1 rounded bg-blue-600 px-1 text-[10px] font-semibold text-white">
          {citation.index}
        </span>
      </div>
      <div className="px-2 py-1.5 min-w-0">
        <div className={cx("truncate text-[11px] font-medium", tc.textPrimary)}>
          {name}
        </div>
        {pos && (
          <div className={cx("truncate text-[10px]", tc.textMuted)}>{pos}</div>
        )}
      </div>
    </button>
  );
};

// ─── List ────────────────────────────────────────────────────────────────────

export const CitationList: React.FC<{ citations?: Citation[] }> = ({
  citations,
}) => {
  const { t } = useTranslation();
  const tc = useThemeClasses();
  const [showAll, setShowAll] = useState(false);
  const openCitation = useCitationViewer((s) => s.openCitation);

  if (!citations || citations.length === 0) return null;
  const visible = showAll ? citations : citations.slice(0, COLLAPSED_COUNT);

  return (
    <section className="mt-2" aria-label={t("cockpit.sources", "Sources")}>
      <div
        className={cx(
          "mb-1 text-[11px] font-semibold uppercase tracking-wide",
          tc.textMuted,
        )}
      >
        {t("cockpit.sources", "Sources")}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {visible.map((c) => (
          <CitationCard
            key={`${c.index}-${citationKey(c)}`}
            citation={c}
            onOpen={(trigger) => openCitation(c, trigger)}
          />
        ))}
      </div>
      {citations.length > COLLAPSED_COUNT && (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className="mt-1.5 text-xs font-medium text-blue-600 hover:underline"
        >
          {showAll
            ? t("cockpit.showFewerSources", "Show fewer")
            : t("cockpit.showAllSources", "Show all {{count}} sources", {
                count: citations.length,
              })}
        </button>
      )}
    </section>
  );
};

// ─── Dialog host ─────────────────────────────────────────────────────────────
// Rendered once at app level, so the open dialog does not depend on which
// CitationList (streaming answer or finished message) opened it.

export const CitationViewerHost: React.FC = () => {
  const citation = useCitationViewer((s) => s.citation);
  if (!citation) return null;
  const close = () => {
    const { trigger, closeCitation } = useCitationViewer.getState();
    closeCitation();
    // Return focus to the card that opened the dialog; after the streaming
    // hand-off that card is gone, so use the same source's card in the
    // finished message instead.
    const key = citationKey(citation);
    const target = trigger?.isConnected
      ? trigger
      : Array.from(
          document.querySelectorAll<HTMLElement>("[data-citation-key]"),
        )
          .filter((el) => el.dataset.citationKey === key)
          .pop();
    target?.focus();
  };
  return (
    <CitationViewer
      key={citationKey(citation)}
      citation={citation}
      onClose={close}
    />
  );
};

export default CitationList;
